import { useQuery, useQueryClient } from "@tanstack/react-query";
import { RegistrarLigacaoModal } from "../components/RegistrarLigacaoModal";
import { useAuth, UserRole } from "@/features/auth";
import { ROLES } from "@/features/auth/types";
import { ClienteDetalhesModal } from "../components/ClienteDetalhesModal";

import { useProfileSwitcher } from "@/hooks/useProfileSwitcher";
import { Link, useNavigate } from "react-router-dom";
import { useState, useEffect, useMemo } from "react";
import { ClientesService } from "../services/clientes.service";
import type { Cliente, TemperaturaLead } from "../types/cliente";
import type { ListarClientesResponse } from "../services/clientes.service";
import { formatCpfCnpj, formatPhone } from "@/utils/format";
import { supabase } from "@/integrations/supabase/client";
import { getMembrosDoTimeSupervisor } from "@/utils/teamUtils";
import { toast } from "sonner";

// Ant Design
import {
  Table,
  Card,
  Input,
  Select,
  Button,
  Tag,
  Space,
  Badge,
  Typography,
  Tooltip,
  Popconfirm,
  Popover,
  Statistic,
  Row,
  Col,
  Flex,
  Empty,
  App,
  Divider,
  Spin,
} from "antd";
import {
  PlusOutlined,
  SearchOutlined,
  DeleteOutlined,
  PhoneOutlined,
  MailOutlined,
  CalendarOutlined,
  TeamOutlined,
  RiseOutlined,
  ThunderboltOutlined,
  FilterOutlined,
  EditOutlined,
  FireOutlined,
  ExperimentOutlined,
  CloudOutlined,
  BankOutlined,
  MoreOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";

import { PageHeader } from "@/components/layout/PageHeader";
import { useProductionStartDay } from "@/hooks/useProductionStartDay";
import { getProductionPeriod, getRecentProductionPeriods } from "@/utils/productionPeriod";
import { useProductionPeriodFilter } from "@/hooks/useProductionPeriodFilter";

const { Text } = Typography;

// ============================================================
// Helpers (preservados da versão original)
// ============================================================

async function getLideresDoMesmoTime(relacionadorUserId: string | undefined): Promise<string[]> {
  if (!relacionadorUserId) return [];

  try {
    const { data: relacionador, error: relacionadorError } = await supabase
      .from('consultores')
      .select('id, supervisor_id')
      .eq('user_id', relacionadorUserId)
      .eq('tipo', 'relacionador')
      .single();

    if (relacionadorError || !relacionador) {
      console.error('Erro ao buscar relacionador:', relacionadorError);
      return [];
    }

    const { data: lideres, error: lideresError } = await supabase
      .from('consultores')
      .select('user_id')
      .eq('supervisor_id', (relacionador as any).supervisor_id)
      .eq('tipo', 'lider')
      .eq('status', 'ativo');

    if (lideresError) {
      console.error('Erro ao buscar líderes do time:', lideresError);
      return [];
    }

    const userIds = (lideres as any[]).map(l => l.user_id).filter(Boolean);
    userIds.push(relacionadorUserId);

    return userIds;
  } catch (error) {
    console.error('Erro ao buscar líderes do mesmo time:', error);
    return [];
  }
}

// ============================================================
// Temperatura helpers
// ============================================================

function renderTemperaturaTag(temp: TemperaturaLead | undefined) {
  switch (temp) {
    case 'QUENTE':
      return <Tag icon={<FireOutlined />} className="rounded-full px-3 bg-red-50 text-red-600 border-red-100 dark:bg-red-500/10 dark:text-red-400 dark:border-red-500/20 font-bold uppercase text-[0.65rem]">Quente</Tag>;
    case 'MEDIO':
      return <Tag icon={<ExperimentOutlined />} className="rounded-full px-3 bg-amber-50 text-amber-600 border-amber-100 dark:bg-amber-500/10 dark:text-amber-400 dark:border-amber-500/20 font-bold uppercase text-[0.65rem]">Médio</Tag>;
    case 'FRIO':
      return <Tag icon={<CloudOutlined />} className="rounded-full px-3 bg-cyan-50 text-cyan-600 border-cyan-100 dark:bg-cyan-500/10 dark:text-cyan-400 dark:border-cyan-500/20 font-bold uppercase text-[0.65rem]">Frio</Tag>;
    default:
      return <Tag className="rounded-full px-3 font-bold uppercase text-[0.65rem] dark:bg-zinc-800 dark:text-zinc-400 dark:border-zinc-700">S/I</Tag>;
  }
}

// ============================================================
// Page Component
// ============================================================

export default function ClientesPage() {
  const { profile } = useAuth();
  const { getCurrentProfile } = useProfileSwitcher();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [temperaturaFilter, setTemperaturaFilter] = useState<TemperaturaLead | "TODOS">("TODOS");
  const [isDeletingBulk, setIsDeletingBulk] = useState(false);
  const [viewingCliente, setViewingCliente] = useState<Cliente | null>(null);
  const [activePopover, setActivePopover] = useState<string | null>(null);
  // Secretária e Pós-Venda não trabalham com ciclo de produção: começam sem filtro (todos os clientes)
  const { productionPeriodFilter, setProductionPeriodFilter } = useProductionPeriodFilter(
    getCurrentProfile !== ROLES.SECRETARIA && getCurrentProfile !== ROLES.POS_VENDA
  );
  const startDay = useProductionStartDay();
  const PER_PAGE = 10;

  const currentRole = getCurrentProfile;

  // ---- Data fetching (lógica de negócio preservada) ---------
  const { data: response, isLoading } = useQuery<ListarClientesResponse>({
    queryKey: ["clientes", profile?.tenant_id || "", profile?.role === UserRole.SUPER_ADMIN ? "all" : "tenant", page, search, currentRole, temperaturaFilter, productionPeriodFilter, startDay],
    queryFn: async () => {
      const isRelacionador = currentRole === 'RELACIONADOR';
      const isSupervisor = currentRole === 'SUPERVISOR';

      let filterByUser: string | undefined = undefined;

      if (currentRole === ROLES.LIDER_VENDAS) {
        try {
          const { data: consultorAtual } = await supabase
            .from('consultores')
            .select('supervisor_id')
            .eq('user_id', profile?.uuid || '')
            .eq('tipo', 'lider')
            .single();

          if (consultorAtual?.supervisor_id) {
            const { data: relacionadores } = await supabase
              .from('consultores')
              .select('user_id')
              .eq('supervisor_id', consultorAtual.supervisor_id)
              .eq('tipo', 'relacionador')
              .eq('status', 'ativo');

            if (relacionadores && relacionadores.length > 0) {
              const userIds = relacionadores.map(r => r.user_id);
              if (profile?.uuid && !userIds.includes(profile.uuid)) {
                userIds.push(profile.uuid);
              }
              filterByUser = userIds.join(',');
            } else {
              filterByUser = profile?.uuid;
            }
          } else {
            filterByUser = profile?.uuid;
          }
        } catch (error) {
          console.error('Erro ao buscar relacionadores da equipe:', error);
          filterByUser = profile?.uuid;
        }
      } else if (currentRole === ROLES.SUPERVISOR) {
        const membrosDoTime = await getMembrosDoTimeSupervisor(profile?.uuid);
        filterByUser = membrosDoTime.join(',');
      } else if (currentRole === ROLES.USER || currentRole === 'VENDEDOR') {
        filterByUser = profile?.uuid;
      } else if (currentRole === ROLES.RELACIONADOR) {
        // Relacionador filtra por ele mesmo no servidor e depois aplica lógica local de contratos recebidos
        filterByUser = profile?.uuid;
      }

      let baseData: any[] = [];
      let totalCount = 0;
      let totalPages = 0;

      // ---- 1. Busca de Dados Base ----
      if (productionPeriodFilter) {
        // Se houver filtro de produção, buscamos uma amostragem maior (1000)
        const responseData = await ClientesService.listarClientes(1, 1000, search, profile?.tenant_id ? String(profile.tenant_id) : undefined, filterByUser);
        baseData = responseData.data || [];
        totalCount = responseData.total;
        totalPages = responseData.total_pages;
      } else {
        // Fluxo normal paginado
        const responseData = await ClientesService.listarClientes(page, PER_PAGE, search, profile?.tenant_id ? String(profile.tenant_id) : undefined, filterByUser);
        baseData = responseData.data || [];
        totalCount = responseData.total;
        totalPages = responseData.total_pages;
      }

      let filtered = [...baseData];

      // ---- 2. Filtro: Período de Produção ----
      if (productionPeriodFilter) {
        const range = getProductionPeriod(productionPeriodFilter.month, productionPeriodFilter.year, startDay);
        filtered = filtered.filter((cliente: any) => {
          const clientCreated = cliente.created_at ? new Date(cliente.created_at) : null;
          const isInPeriod = clientCreated && clientCreated >= range.startDate && clientCreated <= range.endDate;
          
          const hasContractInPeriod = (cliente.contratos || []).some((c: any) => {
            const cDate = c.created_at ? new Date(c.created_at) : null;
            return cDate && cDate >= range.startDate && cDate <= range.endDate;
          });

          return isInPeriod || hasContractInPeriod;
        });
      }

        // Filtragem para Relacionadores (Lógica baseada em contratos vinculados)
        if (isRelacionador) {
        const lideresDoTime = await getLideresDoMesmoTime(profile?.uuid);

        const { data: contratosProprios } = await supabase
          .from('contratos')
          .select('cliente_id, created_by, status')
          .eq('tenant_id', profile?.tenant_id || 1)
          .eq('created_by', profile?.uuid || '');

        const { data: contratosLideres } = await supabase
          .from('contratos')
          .select('cliente_id, created_by, status')
          .eq('tenant_id', profile?.tenant_id || 1)
          .in('created_by', lideresDoTime)
          .in('status', ['enviado_para_relacionador', 'ENVIADO_PARA_RELACIONADOR']);

        const contratosPermitidos = [
          ...(contratosProprios || []),
          ...(contratosLideres || [])
        ];

        const clientesComContratosPermitidosIds = new Set(
          contratosPermitidos?.map(c => c.cliente_id).filter(Boolean) || []
        );

        filtered = filtered.filter((cliente: any) => {
          const isOwnClient = cliente.created_by === profile?.uuid;
          const hasContract = clientesComContratosPermitidosIds.has(parseInt(cliente.id));
          return isOwnClient || hasContract;
        });
      }

        // Filtragem para Supervisores (Reforço local)
        if (isSupervisor) {
        const membrosDoTime = await getMembrosDoTimeSupervisor(profile?.uuid);

        filtered = filtered.filter((cliente: any) => {
          return membrosDoTime.includes(cliente.created_by) || cliente.created_by === profile?.uuid;
        });
      }

      // ---- 5. Retorno Final ----
      if (productionPeriodFilter) {
        return {
          data: filtered,
          total: filtered.length,
          page: 1,
          per_page: filtered.length,
          total_pages: 1
        };
      }

      return {
        data: filtered,
        total: totalCount,
        page: page,
        per_page: PER_PAGE,
        total_pages: totalPages
      };
    },
    enabled: !!profile,
  });

  const clientes = response?.data || [];
  const totalClientes = response?.total || 0;

  // Limpar seleção ao mudar busca/página
  useEffect(() => {
    setSelectedRowKeys([]);
  }, [search, page]);

  // Filtro local por temperatura
  const clientesFiltrados = useMemo(() => {
    return clientes.filter((c) => temperaturaFilter === "TODOS" || c.temperatura_lead === temperaturaFilter);
  }, [clientes, temperaturaFilter]);

  // Estatísticas
  const currentMonth = new Date().getMonth();
  const currentYear = new Date().getFullYear();

  const novosClientes = useMemo(() => {
    return clientesFiltrados.filter((c) => {
      if (!c.created_at || isNaN(new Date(c.created_at).getTime())) return false;
      const d = new Date(c.created_at);
      return d.getMonth() === currentMonth && d.getFullYear() === currentYear;
    }).length;
  }, [clientesFiltrados, currentMonth, currentYear]);

  const clientesAtivos = useMemo(() => {
    return clientesFiltrados.filter((c) => c.contratos && c.contratos.length > 0).length;
  }, [clientesFiltrados]);

  // Role‑specific
  const roleMetrics = useMemo(() => {
    switch (currentRole) {
      case 'LIDER_VENDAS':
        return { title: "Meus Clientes", subtitle: "Gerencie seus clientes e crie novos contratos" };
      case 'SECRETARIA':
        return { title: "Clientes - Revisão", subtitle: "Valide dados e documentos dos clientes" };
      default:
        return { title: "Clientes", subtitle: "Gerencie todos os clientes da empresa" };
    }
  }, [currentRole]);

  // ---- Bulk delete -------------------------------------------
  const handleBulkDelete = async () => {
    if (selectedRowKeys.length === 0) return;
    try {
      setIsDeletingBulk(true);
      const ids = selectedRowKeys.map(String);
      const result = await ClientesService.excluirClientesEmLote(ids);
      queryClient.invalidateQueries({ queryKey: ["clientes"] });
      setSelectedRowKeys([]);
      toast.success(result.message);
    } catch (err) {
      console.error("Erro ao excluir clientes:", err);
      toast.error("Erro ao excluir clientes. Tente novamente.");
    } finally {
      setIsDeletingBulk(false);
    }
  };

  // ---- Single delete -----------------------------------------
  const handleDelete = async (clienteId: string, clienteNome: string) => {
    try {
      await ClientesService.excluirCliente(clienteId);
      queryClient.invalidateQueries({ queryKey: ["clientes"] });
      toast.success(`Cliente "${clienteNome}" excluído com sucesso`);
      setActivePopover(null);
    } catch (err) {
      console.error("Erro ao excluir cliente:", err);
      toast.error("Erro ao excluir cliente. Tente novamente.");
    }
  };

  // ---- Table columns -----------------------------------------
  const columns: ColumnsType<Cliente> = [
    {
      title: "Cliente",
      dataIndex: "nome",
      key: "nome",
      ellipsis: true,
      sorter: (a, b) => (a.nome || "").localeCompare(b.nome || ""),
      render: (nome: string, record) => (
        <Space orientation="vertical" size={0}>
          <Link to={`/clientes/${record.id}/editar`}>
            <Text strong style={{ fontSize: 14 }} className="hover:text-blue-600 transition-colors">
              {nome}
            </Text>
          </Link>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {formatCpfCnpj(record.cpf)}
          </Text>
        </Space>
      ),
    },
    {
      title: "Contato",
      key: "contato",
      responsive: ['md'] as any,
      render: (_: any, record) => (
        <Space orientation="vertical" size={0}>
          {record.email && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              <MailOutlined style={{ marginRight: 4 }} />
              {record.email}
            </Text>
          )}
          {record.tel1 && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              <PhoneOutlined style={{ marginRight: 4 }} />
              {formatPhone(record.tel1)}
            </Text>
          )}
        </Space>
      ),
    },
    {
      title: "Temperatura",
      dataIndex: "temperatura_lead",
      key: "temperatura",
      width: 150,
      align: "center",
      filters: [
        { text: "🔥 Quente", value: "QUENTE" },
        { text: "🌡️ Médio", value: "MEDIO" },
        { text: "❄️ Frio", value: "FRIO" },
      ],
      onFilter: (value, record) => record.temperatura_lead === value,
      render: (temp: TemperaturaLead) => renderTemperaturaTag(temp),
    },
    {
      title: "Contratos",
      key: "contratos",
      width: 120,
      align: "center",
      sorter: (a, b) => (a.contratos?.length || 0) - (b.contratos?.length || 0),
      render: (_: any, record) => {
        const count = record.contratos?.length || 0;
        return count > 0 ? (
          <Badge count={count} color="green" overflowCount={99} />
        ) : (
          <Text type="secondary">—</Text>
        );
      },
    },
    {
      title: "Cadastro",
      dataIndex: "created_at",
      key: "created_at",
      width: 130,
      responsive: ['lg'] as any,
      sorter: (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
      render: (date: string) => {
        if (!date || isNaN(new Date(date).getTime())) return <Text type="secondary">—</Text>;
        return (
          <Text type="secondary" style={{ fontSize: 12 }}>
            <CalendarOutlined style={{ marginRight: 4 }} />
            {new Date(date).toLocaleDateString("pt-BR")}
          </Text>
        );
      },
    },
    ...(profile?.role === UserRole.SUPER_ADMIN
      ? [
        {
          title: "Empresa",
          key: "tenant",
          width: 140,
          responsive: ['xl'] as any,
          render: (_: any, record: Cliente) =>
            record.tenant ? (
              <Text type="secondary" style={{ fontSize: 12 }}>
                <BankOutlined style={{ marginRight: 4 }} />
                {record.tenant.nome}
              </Text>
            ) : (
              <Text type="secondary">—</Text>
            ),
        } as any,
      ]
      : []),
    ...((currentRole === 'RELACIONADOR' || currentRole === 'ASSOCIADO' || currentRole === 'SUPER_ADMIN')
      ? [
        {
          title: "Ligações",
          key: "ligacoes",
          width: 110,
          align: "center" as const,
          render: (_: any, record: Cliente) => (
            <Tooltip title={record.ultima_ligacao ? `Última: ${new Date(record.ultima_ligacao).toLocaleDateString('pt-BR')}` : 'Nenhuma ligação'}>
              <Tag color="purple">{record.ligacoes_realizadas || 0}</Tag>
            </Tooltip>
          ),
        } as any,
      ]
      : []),
    ...(profile?.role !== UserRole.SUPER_ADMIN
      ? [
        {
          title: "",
          key: "acoes",
          width: 50,
          align: "center" as const,
          fixed: 'right' as const,
          render: (_: any, record: Cliente) => {
            const popoverContent = (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {(currentRole === 'RELACIONADOR' || currentRole === 'ASSOCIADO' || currentRole === 'SUPER_ADMIN') && (
                  <>
                    <RegistrarLigacaoModal
                      clienteId={record.id}
                      clienteNome={record.nome}
                      temperaturaAtual={record.temperatura_lead || 'MEDIO'}
                      trigger={
                        <Button
                          type="text"
                          icon={<PhoneOutlined />}
                          size="small"
                          style={{ textAlign: 'left', justifyContent: 'flex-start', width: '100%' }}
                        >
                          Registrar Ligação
                        </Button>
                      }
                    />
                    <Divider style={{ margin: '2px 0' }} />
                  </>
                )}
                <Button
                  type="text"
                  icon={<EyeOutlined />}
                  size="small"
                  style={{ textAlign: 'left', justifyContent: 'flex-start' }}
                  onClick={() => {
                    setViewingCliente(record);
                    setActivePopover(null);
                  }}
                >
                  Visualizar
                </Button>
                <Button
                  type="text"
                  icon={<EditOutlined />}
                  size="small"
                  style={{ textAlign: 'left', justifyContent: 'flex-start' }}
                  onClick={() => {
                    setActivePopover(null);
                    navigate(`/clientes/${record.id}/editar`);
                  }}
                >
                  Editar
                </Button>
                <Divider style={{ margin: '2px 0' }} />
                <Popconfirm
                  title="Excluir cliente"
                  description={`Excluir "${record.nome}"?`}
                  okText="Excluir"
                  cancelText="Cancelar"
                  okButtonProps={{ danger: true }}
                  onConfirm={() => handleDelete(String(record.id), record.nome)}
                >
                  <Button
                    type="text"
                    danger
                    icon={<DeleteOutlined />}
                    size="small"
                    style={{ textAlign: 'left', justifyContent: 'flex-start' }}
                  >
                    Excluir
                  </Button>
                </Popconfirm>
              </div>
            );

            return (
              <Popover
                open={activePopover === String(record.id)}
                onOpenChange={(visible) => setActivePopover(visible ? String(record.id) : null)}
                content={popoverContent}
                trigger="click"
                placement="bottomRight"
                arrow={false}
                styles={{ container: { padding: '4px' } }}
              >
                <Button
                  type="text"
                  icon={<MoreOutlined style={{ fontSize: 18 }} />}
                  size="small"
                />
              </Popover>
            );
          },
        } as any,
      ]
      : []),
  ];

  // ---- Row selection -----------------------------------------
  const rowSelection = profile?.role !== UserRole.SUPER_ADMIN
    ? {
      selectedRowKeys,
      onChange: (keys: React.Key[]) => setSelectedRowKeys(keys),
    }
    : undefined;

  // ---- Render ------------------------------------------------
  return (
    <App>
      <Spin spinning={isLoading} tip="Carregando clientes...">
        <div className="flex flex-col min-h-screen bg-[#f8fafc] dark:bg-[#141414] transition-colors duration-300">
          <div style={{ maxWidth: 1200, margin: "0 auto", padding: "24px 16px", width: '100%' }}>
          <PageHeader 
          title={roleMetrics.title}
          subtitle={roleMetrics.subtitle}
        >
          {profile?.role !== UserRole.SUPER_ADMIN && (
            <Link to="/clientes/novo">
              <Button 
                type="primary" 
                icon={<PlusOutlined />} 
                size="large"
                className="rounded-[12px] bg-[linear-gradient(135deg,#FF3737_0%,#D92B2B_100%)] border-none shadow-[0_4px_12px_rgba(255,55,55,0.3)] hover:scale-[1.02] transition-all"
              >
                Novo Cliente
              </Button>
            </Link>
          )}
        </PageHeader>

        {/* Stats */}
        <Row gutter={[16, 16]} className="mb-8">
          <Col xs={24} md={8}>
            <Card 
              variant="borderless" 
              className="rounded-[20px] bg-white/70 dark:bg-zinc-900/60 backdrop-blur-md border border-[rgba(255,55,55,0.1)] dark:border-white/10 shadow-sm hover:shadow-md transition-all duration-300"
            >
              <Statistic
                title={<span className="text-[0.65rem] font-bold text-[#64748b] dark:text-zinc-400 uppercase tracking-widest">Total de clientes</span>}
                value={totalClientes}
                prefix={<div className="p-2 rounded-[10px] bg-[#FF3737]/10 dark:bg-[#FF3737]/20 mr-2"><TeamOutlined className="text-[#FF3737] dark:text-[#FF3737]" /></div>}
                styles={{ content: {  color: "inherit", fontWeight: 800, fontSize: '1.75rem' } }}
              />
            </Card>
          </Col>
          <Col xs={24} md={8}>
            <Card 
              variant="borderless" 
              className="rounded-[20px] bg-white/70 dark:bg-zinc-900/60 backdrop-blur-md border border-[rgba(255,55,55,0.1)] dark:border-white/10 shadow-sm hover:shadow-md transition-all duration-300"
            >
              <Statistic
                title={<span className="text-[0.65rem] font-bold text-[#64748b] dark:text-zinc-400 uppercase tracking-widest">Novos este mês</span>}
                value={novosClientes}
                prefix={<div className="p-2 rounded-[10px] bg-[#D92B2B]/10 dark:bg-[#D92B2B]/20 mr-2"><RiseOutlined className="text-[#D92B2B] dark:text-[#FF3737]" /></div>}
                styles={{ content: {  color: "inherit", fontWeight: 800, fontSize: '1.75rem' } }}
              />
            </Card>
          </Col>
          <Col xs={24} md={8}>
            <Card 
              variant="borderless" 
              className="rounded-[20px] bg-white/70 dark:bg-zinc-900/60 backdrop-blur-md border border-[rgba(255,55,55,0.08)] dark:border-white/10 shadow-sm hover:shadow-md transition-all duration-300"
            >
              <Statistic
                title={<span className="text-[0.65rem] font-bold text-[#64748b] dark:text-zinc-400 uppercase tracking-widest">Com contratos</span>}
                value={clientesAtivos}
                prefix={<div className="p-2 rounded-[10px] bg-indigo-50 dark:bg-indigo-500/10 mr-2"><ThunderboltOutlined className="text-indigo-600 dark:text-indigo-400" /></div>}
                styles={{ content: {  color: "inherit", fontWeight: 800, fontSize: '1.75rem' } }}
              />
            </Card>
          </Col>
        </Row>

        {/* Table card */}
        <Card
          variant="borderless"
          className="rounded-[24px] bg-white/60 dark:bg-zinc-950/60 backdrop-blur-lg border border-[rgba(255,55,55,0.08)] dark:border-white/5 shadow-xl overflow-hidden overflow-x-hidden"
          styles={{ body: { padding: 0 } }}
        >
          <div style={{ padding: "16px 24px", borderBottom: "1px solid rgba(255,55,55,0.1)" }}>
            <Flex justify="space-between" align="center" wrap="wrap" gap={12}>
              <Space wrap className="w-full sm:w-auto">
                <Input
                  placeholder={
                    profile?.role === UserRole.SUPER_ADMIN
                      ? "Buscar por nome, CPF, email ou empresa..."
                      : "Buscar por nome, CPF ou email..."
                  }
                  value={search}
                  onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                  allowClear
                  className="w-full sm:w-[340px] rounded-[12px] h-10 border-[rgba(255,55,55,0.15)] dark:border-zinc-800 dark:bg-zinc-900 focus:border-[#FF3737] focus:shadow-[0_0_0_2px_rgba(255,55,55,0.1)] transition-all"
                  prefix={<SearchOutlined className="text-[#FF3737]/70 dark:text-[#FF3737]/70" />}
                />
                <Select
                  placeholder="Período de Produção"
                  allowClear
                  className="w-full sm:w-[220px] h-10 transition-all rounded-[12px] dark:bg-zinc-900 border-none"
                  value={productionPeriodFilter ? `${productionPeriodFilter.month}-${productionPeriodFilter.year}` : undefined}
                  onChange={(val) => {
                    if (!val) {
                      setProductionPeriodFilter(null);
                    } else {
                      const [month, year] = val.split('-').map(Number);
                      setProductionPeriodFilter({ month, year });
                    }
                    setPage(1);
                  }}
                  options={getRecentProductionPeriods(12, startDay).map((p: any) => ({
                    label: p.label,
                    value: `${p.month}-${p.year}`
                  }))}
                />
                <Select
                  value={temperaturaFilter}
                  onChange={(val) => { setTemperaturaFilter(val); setPage(1); }}
                  className="w-full sm:w-[180px] h-10 transition-all rounded-[12px] dark:bg-zinc-900 border-none"
                  dropdownClassName="rounded-[12px] dark:bg-zinc-900 dark:border-zinc-800"
                  suffixIcon={<FilterOutlined className="text-[#FF3737]/70 dark:text-[#FF3737]/70" />}
                  options={[
                    { value: "TODOS", label: "Todas temperaturas" },
                    { value: "QUENTE", label: "🔥 Quentes" },
                    { value: "MEDIO", label: "🌡️ Médios" },
                    { value: "FRIO", label: "❄️ Frios" },
                  ]}
                />
              </Space>

              {profile?.role !== UserRole.SUPER_ADMIN && selectedRowKeys.length > 0 && (
                <Popconfirm
                  title="Excluir clientes selecionados"
                  description={`Tem certeza que deseja excluir ${selectedRowKeys.length} cliente(s)? Esta ação não pode ser desfeita.`}
                  okText="Excluir"
                  cancelText="Cancelar"
                  okButtonProps={{ danger: true, loading: isDeletingBulk }}
                  onConfirm={handleBulkDelete}
                >
                  <Button danger icon={<DeleteOutlined />} loading={isDeletingBulk}>
                    Excluir selecionados ({selectedRowKeys.length})
                  </Button>
                </Popconfirm>
              )}
            </Flex>
          </div>

          {/* Table */}
          <Table<Cliente>
            rowKey="id"
            columns={columns}
            dataSource={clientesFiltrados}
            rowSelection={rowSelection}
            loading={isLoading}
            scroll={{ x: 1000 }}
            pagination={{
              current: page,
              pageSize: PER_PAGE,
              total: totalClientes,
              showSizeChanger: false,
              showTotal: (total) => `${total} clientes`,
              onChange: (p) => setPage(p),
              style: { padding: "0 24px 16px" },
            }}
            locale={{
              emptyText: (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={
                    search
                      ? "Nenhum cliente encontrado. Tente ajustar os termos de busca."
                      : "Comece cadastrando seu primeiro cliente."
                  }
                >
                  {profile?.role !== UserRole.SUPER_ADMIN && !search && (
                    <Link to="/clientes/novo">
                      <Button type="primary" icon={<PlusOutlined />}>
                        Cadastrar Cliente
                      </Button>
                    </Link>
                  )}
                </Empty>
              ),
            }}
            onRow={(record) => ({
              style: { cursor: "pointer" },
              onDoubleClick: () => navigate(`/clientes/${record.id}/editar`),
            })}
          />
        </Card>

        {/* Modal de Visualização do Cliente */}
        {/* Modal de Visualização do Cliente (Novo) */}
        <ClienteDetalhesModal
          clienteId={viewingCliente?.id || null}
          isOpen={!!viewingCliente}
          onClose={() => setViewingCliente(null)}
        />

        </div>
        </div>
      </Spin>
    </App>
  );
}